import React, { useCallback, useEffect } from 'react'
import PropTypes from 'prop-types'
import { Controller, useForm } from 'react-hook-form'
import RoundedModal from '../../../../../molecules/RoundedModal'
import SydLabel, { hookFormErrorAdapter } from '../../../../../commonDesign/SydLabel'
import SydInput from '../../../../../commonDesign/SydInput'
import SydTextArea from '../../../../../commonDesign/SydTextArea'
import Switch from '../../../../../molecules/Switch'
import SydModalActions from '../../../../../commonDesign/SydModal/SydModalActions'
import SydButton from '../../../../../commonDesign/Button'
import { useUpdateStatusTemplateMetadataTypeMutation } from '../../../../../../api/groups'

function EditMetadataTypeDialog ({ open, onClose, metadataType }) {
  const { mutateAsync: updateMetadataType, isLoading } = useUpdateStatusTemplateMetadataTypeMutation()

  const form = useForm({
    mode: 'onChange',
    defaultValues: {
      displayName: '',
      description: '',
      ordinal: 0,
      showInListPage: false
    }
  })

  const { handleSubmit, reset } = form

  useEffect(() => {
    if (metadataType) {
      reset({
        displayName: metadataType.displayName || '',
        description: metadataType.description || '',
        ordinal: metadataType.ordinal || 0,
        showInListPage: metadataType.showInListPage || false
      })
    }
  }, [metadataType, reset])

  const handleClose = useCallback(() => {
    reset()
    onClose()
  }, [reset, onClose])

  const onSubmit = useCallback(async (formData) => {
    try {
      await updateMetadataType({
        metadataTypeId: metadataType.metadataTypeId,
        displayName: formData.displayName,
        description: formData.description || null,
        ordinal: parseInt(formData.ordinal, 10),
        showInListPage: formData.showInListPage
      })
      handleClose()
    } catch (error) {
      console.error('Failed to update metadata type:', error)
    }
  }, [updateMetadataType, metadataType, handleClose])

  if (!metadataType) return null

  return (
    <RoundedModal
      title='Edit Metadata Field'
      subtitle={`Editing: ${metadataType.metadataKey} (${metadataType.valueType})`}
      open={open}
      onClose={handleClose}
      position='top'
    >
      <form onSubmit={handleSubmit(onSubmit)} className='flex flex-col gap-5'>
        <Controller
          name='displayName'
          control={form.control}
          rules={{ required: { value: true, message: 'Display Name is Required' } }}
          render={({ field, fieldState }) => (
            <SydLabel
              label='Display Name'
              required
              error={hookFormErrorAdapter(form, fieldState)}
            >
              <SydInput
                {...field}
                disabled={isLoading}
                size='sm'
              />
            </SydLabel>
          )}
        />

        <Controller
          name='description'
          control={form.control}
          render={({ field, fieldState }) => (
            <SydLabel
              label='Description'
              error={hookFormErrorAdapter(form, fieldState)}
            >
              <SydTextArea
                {...field}
                disabled={isLoading}
                rows={4}
              />
            </SydLabel>
          )}
        />

        <Controller
          name='ordinal'
          control={form.control}
          render={({ field, fieldState }) => (
            <SydLabel
              label='Display Order'
              description='Lower numbers appear first'
              error={hookFormErrorAdapter(form, fieldState)}
            >
              <SydInput
                {...field}
                disabled={isLoading}
                type='number'
                size='sm'
              />
            </SydLabel>
          )}
        />

        <Controller
          name='showInListPage'
          control={form.control}
          render={({ field, fieldState }) => (
            <div
              className='row flex gap-2 items-center'
              style={{ cursor: 'pointer', pointerEvents: isLoading ? 'none' : 'auto' }}
              onClick={() => field.onChange(!field.value)}
            >
              <Switch
                {...field}
                checked={field.value}
                size='sm'
                disabled={isLoading}
              />
              <SydLabel
                label='Show In List Page'
                description='Will surface this value (if set) in the list of data'
                error={hookFormErrorAdapter(form, fieldState)}
              />
            </div>
          )}
        />

        <SydModalActions>
          <SydButton
            onClick={handleClose}
            variant='outline'
            disabled={isLoading}
          >
            Cancel
          </SydButton>
          <SydButton
            type='submit'
            variant='primary'
            processing={isLoading}
          >
            Save Changes
          </SydButton>
        </SydModalActions>
      </form>
    </RoundedModal>
  )
}

EditMetadataTypeDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  metadataType: PropTypes.object
}

export default EditMetadataTypeDialog
